import { PRODUCT_IDS } from "./productContext";

/** i18n language codes supported by the app */
export const SUPPORTED_LOCALES = Object.freeze(["he", "en", "el"]);

/** Public URL prefix (canonical) ↔ i18n language */
export const PUBLIC_PREFIX_TO_I18N = Object.freeze({
  he: "he",
  en: "en",
  el: "el",
  us: "en", // legacy alias — Sprint 1B redirect to /en/
});

export const I18N_TO_PUBLIC_PREFIX = Object.freeze({
  he: "he",
  en: "en",
  el: "el",
});

export const LOCALE_META = Object.freeze({
  he: Object.freeze({
    i18n: "he",
    publicPrefix: "he",
    dashboardBase: "/he/dashboard",
    legacyDashboardBase: "/p/dashboard",
    legacySecurePrefix: "/p",
    direction: "rtl",
  }),
  en: Object.freeze({
    i18n: "en",
    publicPrefix: "en",
    dashboardBase: "/en/dashboard",
    direction: "ltr",
  }),
  el: Object.freeze({
    i18n: "el",
    publicPrefix: "el",
    dashboardBase: "/el/dashboard",
    direction: "ltr",
  }),
});

const PUBLIC_PREFIX_PATTERN = /^\/(he|en|us|el)(?=\/|$)/;

const EXCLUDED_PUBLIC_PREFIX_REDIRECTS = [
  "/p/",
  "/p",
  "/dashboard",
  "/administration",
  "/admin",
  "/debug/",
  "/api/",
];

function normalizePath(pathname) {
  if (!pathname || typeof pathname !== "string") return "/";
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (normalized.length > 1 && normalized.endsWith("/")) {
    return normalized.replace(/\/+$/, "") || "/";
  }
  return normalized;
}

export function normalizeLocale(locale) {
  const lng = String(locale || "he").toLowerCase();
  if (lng.startsWith("en")) return "en";
  if (lng.startsWith("el")) return "el";
  return "he";
}

export function getLocaleMeta(locale) {
  return LOCALE_META[normalizeLocale(locale)] || LOCALE_META.he;
}

export function i18nLanguageToPublicPrefix(language) {
  return I18N_TO_PUBLIC_PREFIX[normalizeLocale(language)] || "he";
}

export function publicPrefixToI18nLanguage(prefix) {
  return PUBLIC_PREFIX_TO_I18N[prefix] || "he";
}

export function isValidPublicPrefix(prefix) {
  return prefix === "he" || prefix === "en" || prefix === "us" || prefix === "el";
}

export function parsePublicLocaleFromPathname(pathname) {
  const path = normalizePath(pathname);
  const match = path.match(PUBLIC_PREFIX_PATTERN);
  if (!match) {
    return { localePrefix: null, pathnameWithoutLocale: path };
  }
  const localePrefix = match[1];
  const pathnameWithoutLocale = path.replace(PUBLIC_PREFIX_PATTERN, "") || "/";
  return { localePrefix, pathnameWithoutLocale };
}

export function isExcludedFromPublicLocalePrefix(pathname) {
  const path = normalizePath(pathname);
  if (PUBLIC_PREFIX_PATTERN.test(path)) return false;
  return EXCLUDED_PUBLIC_PREFIX_REDIRECTS.some(
    (prefix) => path === prefix.replace(/\/$/, "") || path.startsWith(prefix)
  );
}

export function shouldRedirectToPublicLocalePrefix(pathname) {
  const path = normalizePath(pathname);
  if (!path || path === "/") return true;
  if (PUBLIC_PREFIX_PATTERN.test(path)) return false;
  return !isExcludedFromPublicLocalePrefix(path);
}

export function buildPublicLocalizedPath(pathname, localePrefix = "he") {
  const prefix =
    localePrefix === "us" ? "en" : isValidPublicPrefix(localePrefix) ? localePrefix : "he";
  const path = normalizePath(pathname);
  const { pathnameWithoutLocale } = parsePublicLocaleFromPathname(path);
  if (pathnameWithoutLocale === "/") {
    return `/${prefix}/`;
  }
  const stripped = pathnameWithoutLocale.replace(/^\/+/, "");
  return `/${prefix}/${stripped}`;
}

export function switchPublicLocaleInPathname(pathname, targetLocalePrefix) {
  const { pathnameWithoutLocale } = parsePublicLocaleFromPathname(pathname);
  return buildPublicLocalizedPath(pathnameWithoutLocale, targetLocalePrefix);
}

/**
 * Resolve product + locale from pathname.
 * @returns {{ product: import('./productContext').ProductId, locale: string, isLegacyAlias: boolean, pathnameWithoutProduct: string }}
 */
export function resolveProductFromPathname(pathname) {
  const path = normalizePath(pathname);

  if (path.startsWith("/administration") || path.startsWith("/admin/")) {
    return {
      product: PRODUCT_IDS.ADMIN,
      locale: "he",
      isLegacyAlias: false,
      pathnameWithoutProduct: path,
    };
  }

  if (path.startsWith("/p/dashboard") || path === "/p/dashboard") {
    return {
      product: PRODUCT_IDS.AGENT,
      locale: "he",
      isLegacyAlias: true,
      pathnameWithoutProduct: path.replace(/^\/p\/dashboard/, "") || "/",
    };
  }

  if (path.startsWith("/p/")) {
    return {
      product: PRODUCT_IDS.AGENT,
      locale: "he",
      isLegacyAlias: true,
      pathnameWithoutProduct: path.replace(/^\/p/, "") || "/",
    };
  }

  const enDashMatch = path.match(/^\/en\/dashboard(\/.*)?$/);
  if (enDashMatch) {
    return {
      product: PRODUCT_IDS.AGENT,
      locale: "en",
      isLegacyAlias: false,
      pathnameWithoutProduct: enDashMatch[1] || "/",
    };
  }

  const heDashMatch = path.match(/^\/he\/dashboard(\/.*)?$/);
  if (heDashMatch) {
    return {
      product: PRODUCT_IDS.AGENT,
      locale: "he",
      isLegacyAlias: false,
      pathnameWithoutProduct: heDashMatch[1] || "/",
    };
  }

  const elDashMatch = path.match(/^\/el\/dashboard(\/.*)?$/);
  if (elDashMatch) {
    return {
      product: PRODUCT_IDS.AGENT,
      locale: "el",
      isLegacyAlias: false,
      pathnameWithoutProduct: elDashMatch[1] || "/",
    };
  }

  const { localePrefix, pathnameWithoutLocale } = parsePublicLocaleFromPathname(path);
  if (localePrefix) {
    return {
      product: PRODUCT_IDS.PUBLIC,
      locale: publicPrefixToI18nLanguage(localePrefix),
      isLegacyAlias: localePrefix === "us",
      pathnameWithoutProduct: pathnameWithoutLocale,
    };
  }

  return {
    product: PRODUCT_IDS.PUBLIC,
    locale: "he",
    isLegacyAlias: false,
    pathnameWithoutProduct: path,
  };
}

/**
 * Dashboard base path for agent product.
 * Sprint 1–4: Hebrew uses legacy /p/dashboard unless useLegacyAlias=false.
 */
export function resolveAgentDashboardBase(locale, { useLegacyAlias = true } = {}) {
  const lng = normalizeLocale(locale);
  if (lng === "he" && useLegacyAlias) {
    return LOCALE_META.he.legacyDashboardBase;
  }
  return getLocaleMeta(lng).dashboardBase;
}

/** HTTP headers sent to API — capabilities resolved server-side. */
export function buildApiLocaleHeaders({ locale, product }) {
  return {
    "X-HR-Locale": normalizeLocale(locale),
    "X-HR-Product": product,
  };
}
